// src/lib/api/authErrors.ts — Swedish, actionable copy for auth failures.
//
// The API answers in English ("Failed to create user. Please try again."), and
// the app used to render that verbatim under a title of `Error (${status})`.
// Two things were wrong with that: the language, and the advice. Someone who
// already has an account was told to try again — the one thing that cannot
// work. So the mapping here is by CODE first: the code is the API's statement
// of WHAT happened, and only from that can we say what the person should do.
//
// Status is the fallback for codes we have not met yet, and a generic line is
// the fallback for that. Nothing here ever falls through to the raw API
// message, because that message is English by construction.
import type { ApiError } from './apiUtils';

export interface FriendlyAuthError {
    /** Short headline — used as the toast title. */
    title: string;
    /** One sentence the user can act on. */
    description: string;
    /**
     * True when the account already exists, so a form can offer "logga in
     * istället" rather than leaving the person to work it out.
     */
    isExistingAccount?: boolean;
    /**
     * True when the account exists and the password was right, but the address
     * is unconfirmed. The form must offer to resend the link — the API refuses
     * the login (go.jasit.base v1.66.0, APP_REQUIRE_VERIFIED_LOGIN), and telling
     * someone to "check your inbox" for a mail they never received, with no way
     * to ask for another, is a locked-out account.
     */
    needsEmailConfirmation?: boolean;
}

/** The API's code for "that email or phone is taken" (go.jasit.base v1.65.0+). */
export const CODE_USER_ALREADY_EXISTS = 'USER_ALREADY_EXISTS';

/** Login refused because the address is unconfirmed (go.jasit.base v1.66.0+). */
export const CODE_EMAIL_NOT_VERIFIED = 'EMAIL_NOT_VERIFIED';

const BY_CODE: Record<string, FriendlyAuthError> = {
    [CODE_USER_ALREADY_EXISTS]: {
        title: 'Kontot finns redan',
        description: 'Det finns redan ett konto med den här e-postadressen. Logga in i stället, eller använd en annan adress.',
        isExistingAccount: true,
    },
    [CODE_EMAIL_NOT_VERIFIED]: {
        title: 'Bekräfta din e-postadress',
        description: 'Klicka på länken i mejlet vi skickade när du skapade kontot. Sedan kan du logga in.',
        needsEmailConfirmation: true,
    },
    PHONE_NOT_VERIFIED: {
        title: 'Bekräfta ditt telefonnummer',
        description: 'Vi har skickat en kod via SMS. Bekräfta numret för att kunna logga in.',
    },
    INVALID_CREDENTIALS: {
        title: 'Fel e-post eller lösenord',
        description: 'Kontrollera uppgifterna och försök igen.',
    },
    USER_NOT_FOUND: {
        title: 'Kontot hittades inte',
        description: 'Det finns inget konto med den här e-postadressen.',
    },
    // #355 gave this error a second cause. It used to mean one thing — the
    // account was created through Google and never had a password. Now an
    // admin-created employer also has no password until they open their
    // invitation mail, and telling that person to "log in with Google instead"
    // sends them somewhere that cannot help. The copy names both, shortest path
    // first, because the invitation case is the one a brand-new user is in.
    PASSWORDLESS_ACCOUNT: {
        title: 'Kontot har inget lösenord ännu',
        description:
            'Har du fått en inbjudan? Använd länken i mejlet för att välja ett lösenord. ' +
            'Skapade du kontot med Google loggar du in med Google i stället.',
    },
    PASSWORD_LOGIN_DENIED: {
        title: 'Inloggning med lösenord är avstängd',
        description: 'Använd en annan inloggningsmetod.',
    },
    SOCIAL_AUTH_DENIED: {
        title: 'Google-inloggning är avstängd',
        description: 'Logga in med e-post och lösenord i stället.',
    },
    INVALID_TOKEN: {
        title: 'Länken fungerar inte',
        description: 'Länken är ogiltig eller har redan använts. Begär en ny.',
    },
    VERIFICATION_FAILED: {
        title: 'Bekräftelsen misslyckades',
        description: 'Länken kan ha gått ut. Begär en ny bekräftelselänk.',
    },
    PASSWORD_RESET_FAILED: {
        title: 'Lösenordet kunde inte återställas',
        description: 'Länken kan ha gått ut. Begär en ny återställningslänk.',
    },
    INVALID_REFRESH_TOKEN: {
        title: 'Din session har gått ut',
        description: 'Logga in igen för att fortsätta.',
    },
    USER_VALIDATION_ERROR: {
        title: 'Uppgifterna kunde inte sparas',
        description: 'Kontrollera fälten och försök igen.',
    },
    VALIDATION_ERROR: {
        title: 'Uppgifterna kunde inte sparas',
        description: 'Kontrollera fälten och försök igen.',
    },
    RATE_LIMITED: {
        title: 'För många försök',
        description: 'Vänta en stund innan du försöker igen.',
    },
};

const BY_STATUS: Record<number, FriendlyAuthError> = {
    400: { title: 'Något saknas', description: 'Kontrollera uppgifterna och försök igen.' },
    401: { title: 'Fel e-post eller lösenord', description: 'Kontrollera uppgifterna och försök igen.' },
    403: { title: 'Åtkomst nekad', description: 'Ditt konto har inte behörighet till den här åtgärden.' },
    404: { title: 'Kontot hittades inte', description: 'Det finns inget konto med de uppgifterna.' },
    409: { title: 'Uppgiften är upptagen', description: 'Något av det du angav används redan av ett annat konto.' },
    429: { title: 'För många försök', description: 'Vänta en stund innan du försöker igen.' },
    500: { title: 'Något gick fel hos oss', description: 'Försök igen om en stund. Kontakta oss om det kvarstår.' },
    503: { title: 'Tjänsten är inte tillgänglig', description: 'Vi är strax tillbaka. Försök igen om en stund.' },
};

const GENERIC: FriendlyAuthError = {
    title: 'Något gick fel',
    description: 'Försök igen. Kontakta oss om problemet kvarstår.',
};

/**
 * Swedish title + advice for an auth failure. Never returns the API's own
 * message — see the file header for why.
 */
export function friendlyAuthError(error: Pick<ApiError, 'code' | 'statusCode'>): FriendlyAuthError {
    if (error.code && BY_CODE[error.code]) return BY_CODE[error.code];
    // 0 is what the client reports when the request never reached the API —
    // "kontrollera uppgifterna" would be the wrong advice for a dead network.
    if (!error.statusCode) {
        return {
            title: 'Ingen kontakt med servern',
            description: 'Kontrollera din internetanslutning och försök igen.',
        };
    }
    return BY_STATUS[error.statusCode] ?? GENERIC;
}

/** One line for inline form errors, where a separate title has nowhere to go. */
export function friendlyAuthErrorText(error: Pick<ApiError, 'code' | 'statusCode'>): string {
    const { title, description } = friendlyAuthError(error);
    return `${title}. ${description}`;
}
